import { LEVELS } from '@M3/hooks/useTonalPalette';
import {
    argbFromHex,
    Blend,
    Hct,
    hexFromArgb,
    TonalPalette,
} from '@material/material-color-utilities';

export const stringToColor = (string: string) => {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return adjustTonalColor(color, { tone: 40 });
    return color;
};

export const hueFromHex = (color: String) => {
    const hexInt = parseInt(color.replace(/^#/, ''), 16);

    const hctColor = Hct.fromInt(hexInt);
    console.log(hexInt, hctColor.hue);
    return hctColor.hue;
};

const hexToRgb = (hex: string): number[] => {
    const hexValue = hex.replace('#', '');
    const r = parseInt(hexValue.substring(0, 2), 16);
    const g = parseInt(hexValue.substring(2, 4), 16);
    const b = parseInt(hexValue.substring(4, 6), 16);
    return [r, g, b];
};

const rgbToHsl = (r: number, g: number, b: number): number[] => {
    r /= 255;
    g /= 255;
    b /= 255;

    let max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h: number,
        s: number,
        l: number = (max + min) / 2;

    if (max == min) {
        h = s = 0; // achromatic
    } else {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    return [h, s, l];
};

export const hexToHsl = (hex: string): number[] => {
    const [r, g, b] = hexToRgb(hex);
    return rgbToHsl(r, g, b);
};

type Level = (typeof LEVELS)[number];

////////////
interface ColorAdjustmentOptions {
    tone?: number;
    minTone?: number;
    maxTone?: number;
    chroma?: number;
    minChroma?: number;
    maxChroma?: number;
    hue?: number;
    minHue?: number;
    maxHue?: number;
}

/**
 * Adjusts an HCT color based on specified options
 * @param hex The hex color to adjust
 * @param options Options for adjusting tone, chroma, and hue with min/max constraints
 * @returns The adjusted color as a hex string
 */

export function adjustHctColor(
    hex: string,
    options: ColorAdjustmentOptions,
): string {
    const rgb = argbFromHex(hex);
    const hct = Hct.fromInt(rgb);

    // Process tone
    if (options.tone !== undefined) {
        hct.tone = options.tone;
    }
    if (options.minTone !== undefined && hct.tone < options.minTone) {
        hct.tone = options.minTone;
    }
    if (options.maxTone !== undefined && hct.tone > options.maxTone) {
        hct.tone = options.maxTone;
    }

    // Process chroma
    if (options.chroma !== undefined) {
        hct.chroma = options.chroma;
    }
    if (options.minChroma !== undefined && hct.chroma < options.minChroma) {
        hct.chroma = options.minChroma;
    }
    if (options.maxChroma !== undefined && hct.chroma > options.maxChroma) {
        hct.chroma = options.maxChroma;
    }

    // Process hue
    if (options.hue !== undefined) {
        hct.hue = options.hue;
    }
    if (options.minHue !== undefined && hct.hue < options.minHue) {
        hct.hue = options.minHue;
    }
    if (options.maxHue !== undefined && hct.hue > options.maxHue) {
        hct.hue = options.maxHue;
    }

    return hexFromArgb(hct.toInt());
}

interface AdjustTonalColorOptions {
    tone?: number;
    minTone?: number;
    maxTone?: number;
}

export function adjustTonalColor(
    hex: string,
    options: AdjustTonalColorOptions,
): string {
    const rgb = argbFromHex(hex);
    const test = Blend.harmonize(rgb, argbFromHex('006495'));
    const output = hexFromArgb(test);
    // console.log(
    //     `%c   ${output.toUpperCase()}   `,
    //     `background: ${output}; font-size: 2rem; padding: 12px; border-radius: 3px; color: black;`,
    // );

    const hct = Hct.fromInt(rgb);
    // const palette = TonalPalette.fromInt(rgb);
    // console.log(hct.chroma);

    const palette = TonalPalette.fromHueAndChroma(hct.hue, hct.chroma);

    let proccessedColor = rgb;
    // Process tone
    if (options.tone !== undefined) {
        proccessedColor = palette.tone(options.tone);
    } else if (options.minTone !== undefined && hct.tone < options.minTone) {
        proccessedColor = palette.tone(options.minTone);
    }
    if (options.maxTone !== undefined && hct.tone > options.maxTone) {
        proccessedColor = palette.tone(options.maxTone);
    }

    return hexFromArgb(proccessedColor);
}
////////////
export const adjustTone = (hex: string, level: Level) => {
    const rgb = argbFromHex(hex);
    const hct = Hct.fromInt(rgb);
    hct.tone = level;
    return hexFromArgb(hct.toInt());
};

export const maxToneToLevel = (hex: string, level: Level) => {
    if (!hex) return hex;
    const rgb = argbFromHex(hex);
    const hct = Hct.fromInt(rgb);
    if (hct.tone <= level) return hex;
    hct.tone = level;
    return hexFromArgb(hct.toInt());
};
